import { ApolloError, useMutation } from '@apollo/client'

import { LocalFile, MutationCreateObservationArgs, Observation, ObservationInput, ProjectFile, Scalars } from '@types'

import { useCreateFile } from '@hooks/file/create-file.hook'

import { MUTATION_CREATE_OBSERVATION } from '@libs/apollo/mutations/observation.mutation'

import { OBSERVATIONS } from '@libs/apollo/queries/observation.query'
import { PROJECT_FILE_WITH_POSITIONS } from '../../libs/apollo/queries'

type ObservationInputWithoutFiles = Omit<ObservationInput, 'fileIds'>

type ObservationCreateInputWithoutFiles = {
  observation: ObservationInputWithoutFiles
  projectId: Scalars['ID']['input']
}

interface Data {
  createObservation?: Observation
}

interface UseCreateObservationProps {
  onCompleted?: () => void
}

export const useCreateObservation = (
  props?: UseCreateObservationProps,
): {
  createObservation: (input: ObservationCreateInputWithoutFiles, files: LocalFile[]) => void
  loading: boolean
  error: ApolloError | undefined
  data?: Data | null
} => {
  const { createFile } = useCreateFile()

  const [createObservation, { loading, error, data }] = useMutation<Data, MutationCreateObservationArgs>(
    MUTATION_CREATE_OBSERVATION,
    {
      notifyOnNetworkStatusChange: true,
    },
  )

  const _createObservation = async (input: ObservationCreateInputWithoutFiles, files: LocalFile[]) => {
    const fileIds = []
    for (const file of files) {
      const remoteFile = await createFile(file)
      remoteFile?.id && fileIds.push(remoteFile.id)
    }

    try {
      await createObservation({
        onCompleted: props?.onCompleted,
        update(cache, { data }) {
          if (!data?.createObservation || !data.createObservation?.projectFile?.id) return

          const existingData = cache.readQuery({
            query: PROJECT_FILE_WITH_POSITIONS,
            variables: { id: data.createObservation?.projectFile.id },
          }) as { projectFile: ProjectFile }

          if (existingData?.projectFile) {
            cache.writeQuery({
              query: PROJECT_FILE_WITH_POSITIONS,
              variables: { id: data.createObservation.projectFile.id },
              data: {
                projectFile: {
                  ...existingData.projectFile,
                  observations: [data.createObservation, ...(existingData.projectFile.observations || [])],
                },
              },
            })
          }
        },
        variables: {
          input: {
            projectId: input.projectId,
            observation: {
              ...input.observation,
              fileIds,
            },
          },
        },
        refetchQueries: [
          {
            query: OBSERVATIONS,
            variables: {
              projectId: input.projectId,
            },
          },
        ],
      })
    } catch (error) {
      console.error(error)
    }
  }

  return { createObservation: _createObservation, loading, error, data }
}
